import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { TitleLarge, SubtitleMedium } from '../components/Typography';
import { colors } from '../theme/tokens';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/Button';
import { supabase } from '../config/supabase';
import { useToast } from '../contexts/ToastContext';
import { ArrowLeft, User, Envelope, MapPin, Calendar, Crown } from '@phosphor-icons/react';

const Container = styled.div`
  min-height: 100vh;
  padding: 1rem;
  overflow-y: auto;
`;

const Header = styled.div`
  background-color: ${colors.neutral.white};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ContentContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
`;

const UserProfileContainer = styled.div`
  background: ${colors.neutral.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  padding: 2rem;
  margin-bottom: 1rem;
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
`;

const ProfileImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid ${colors.neutral.offWhite};
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const UserName = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${colors.brand.laurel};
  margin: 0 0 0.5rem 0;
`;

const UserEmail = styled.p`
  font-size: 1.1rem;
  color: ${colors.neutral.charcoal};
  margin: 0 0 0.5rem 0;
`;

const MembershipBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${colors.brand.laurel};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
`;

const DetailCard = styled.div`
  background: ${colors.neutral.offWhite};
  padding: 1.5rem;
  border-radius: 8px;
  border-left: 4px solid ${colors.brand.laurel};
`;

const DetailTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${colors.neutral.darkCharcoal};
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailContent = styled.div`
  color: ${colors.neutral.charcoal};
  line-height: 1.6;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${colors.neutral.lightCharcoal};
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: 500;
  color: ${colors.neutral.darkCharcoal};
`;

const DetailValue = styled.span`
  color: ${colors.neutral.charcoal};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.neutral.charcoal};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.reds.bright};
`;

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = async () => {
    if (!authUser?.id || !isAuthenticated || !id) {
      setError('User not authenticated or invalid user ID');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First, verify the user session is still valid
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Authentication session expired');
      }

      // Get the creator's ID from CreatorDetails using the auth user's ID
      const { data: creatorDetails, error: creatorError } = await supabase
        .from('CreatorDetails')
        .select('id')
        .eq('AuthID', authUser.id)
        .single();

      if (creatorError) {
        throw new Error('Failed to verify creator account');
      }

      if (!creatorDetails) {
        throw new Error('No creator details found for user');
      }
      
      const creatorId = creatorDetails.id;

      // Fetch the specific user from EndUserDetails
      const { data, error } = await supabase
        .from('EndUserDetails')
        .select('*')
        .eq('id', id)
        .eq('CreatorID', creatorId)
        .single();

      if (error) {
        console.error('Error fetching user details:', error);
        if (error.code === 'PGRST116') {
          throw new Error('User not found');
        }
        throw new Error('Failed to fetch user details');
      }

      if (!data) {
        throw new Error('User not found');
      }

      setUser(data);
    } catch (error) {
      console.error('Error in fetchUser:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authUser && isAuthenticated && id) {
      fetchUser();
    } else if (!isAuthenticated) {
      setError('Please log in to view user details');
      setLoading(false);
    }
  }, [authUser, isAuthenticated, id]);

  if (!isAuthenticated) {
    return (
      <Container>
        <Header>
          <HeaderContent>
            <BackButton onClick={() => navigate('/users')}>
              <ArrowLeft size={20} />
              Back to Users
            </BackButton>
          </HeaderContent>
        </Header>
        <ContentContainer>
          <ErrorMessage>Please log in to view user details</ErrorMessage>
        </ContentContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderContent>
          <BackButton onClick={() => navigate('/users')}>
            <ArrowLeft size={20} />
            Back to Users
          </BackButton>
          <ActionButtons>
            <Button>Edit User</Button>
            <Button color="danger">Delete User</Button>
          </ActionButtons>
        </HeaderContent>
      </Header>
      <ContentContainer>
        {loading ? (
          <LoadingMessage>Loading user details...</LoadingMessage>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : user ? (
          <UserProfileContainer>
            <ProfileHeader>
              <ProfileImage 
                src={user.ProfilePicture || 'https://placehold.co/120x120'} 
                alt="Profile Picture" 
              />
              <ProfileInfo>
                <UserName>
                  {user.FirstName && user.LastName 
                    ? `${user.FirstName} ${user.LastName}` 
                    : 'Unnamed User'
                  }
                </UserName>
                <UserEmail>
                  <Envelope size={16} style={{ marginRight: '0.5rem' }} />
                  {user.email || 'No email provided'}
                </UserEmail>
                {user.MembershipType && (
                  <MembershipBadge>
                    <Crown size={16} />
                    {user.MembershipType} Membership
                  </MembershipBadge>
                )}
              </ProfileInfo>
            </ProfileHeader>

            <DetailsGrid>
              <DetailCard>
                <DetailTitle>
                  <User size={20} />
                  Personal Information
                </DetailTitle>
                <DetailContent>
                  <DetailItem>
                    <DetailLabel>Full Name</DetailLabel>
                    <DetailValue>
                      {user.FirstName && user.LastName 
                        ? `${user.FirstName} ${user.LastName}` 
                        : 'Not provided'
                      }
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Email</DetailLabel>
                    <DetailValue>{user.email || 'Not provided'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Country</DetailLabel>
                    <DetailValue>{user.country || 'Not provided'}</DetailValue>
                  </DetailItem>
                </DetailContent>
              </DetailCard>

              <DetailCard>
                <DetailTitle>
                  <Crown size={20} />
                  Membership Details
                </DetailTitle>
                <DetailContent>
                  <DetailItem>
                    <DetailLabel>Membership Type</DetailLabel>
                    <DetailValue>{user.MembershipType || 'No membership'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Status</DetailLabel>
                    <DetailValue>{user.Active ? 'Active' : 'Inactive'}</DetailValue>
                  </DetailItem>
                </DetailContent>
              </DetailCard>

              <DetailCard>
                <DetailTitle>
                  <Calendar size={20} />
                  Account Information
                </DetailTitle>
                <DetailContent>
                  <DetailItem>
                    <DetailLabel>Member Since</DetailLabel>
                    <DetailValue>
                      {user.created_at 
                        ? new Date(user.created_at).toLocaleDateString() 
                        : 'Unknown'
                      }
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Last Updated</DetailLabel>
                    <DetailValue>
                      {user.updated_at 
                        ? new Date(user.updated_at).toLocaleDateString() 
                        : 'Unknown'
                      }
                    </DetailValue>
                  </DetailItem>
                </DetailContent>
              </DetailCard>
            </DetailsGrid>
          </UserProfileContainer>
        ) : (
          <ErrorMessage>User not found</ErrorMessage>
        )}
      </ContentContainer>
    </Container>
  );
} 